import {
  BKJIconTooltip,
  BKJPillDropdown,
  ErrorBoundary,
  Flex,
} from '@awais4123/fillify-design-library';
import i18next from 'i18next';
import { LocationOptionType } from 'components/LocationFilterInput/LocationFilterInput.types';
import {
  SecondaryLocationFilterDataProvider,
  useSecondaryLocationFilterDataContext,
} from 'components/JaneModal/views/EditJaneView/SecondaryLocationFilterInput.context';
import { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  LabelText,
  SecondaryLocationSelectionLabel,
  TooltipWrapper,
} from 'components/JaneModal/views/EditJaneView/EditJaneView.styled';
import { useTranslation } from 'hooks/useTranslation';
import { ChangeFunctionType } from '@awais4123/fillify-design-library/lib/common.types';
import { useSelector } from 'react-redux';
import { isMultiLocationTieringFeatureEnabledSelector } from 'store/selectors/featureFlagSelectors';
import { TeamMemberPageContext } from 'pages/TeamMemberPage/TeamMemberPage.context';
import { BKJRadioInput } from 'components/BKJRadioInput/BKJRadioInput';
import { HARDCODED_SHOW_SCHEDULING_OR_CALLOUT_ON_JANE_EDIT_VIEW_FOR_SECONDARY_LOCATIONS } from 'constants/hardcoded';

interface ISecondaryLocationSectionProps {
  onChange: ChangeFunctionType;
  tieringValueChange: ChangeFunctionType;
  tieringName: string;
  tieringValue: { [key: string]: string };
  dropdownName: string;
  dropdownValue: string[];
}

const mergeOptions = (currentOptions: LocationOptionType[], newOptions: LocationOptionType[]) => {
  const merged = new Map(currentOptions.map((option) => [option.id, option]));

  newOptions.forEach((option) => {
    merged.set(option.id, option); // This will overwrite duplicates
  });

  return Array.from(merged.values());
};

export const SecondaryLocationSectionComponent = (props: ISecondaryLocationSectionProps) => {
  const { onChange, tieringValueChange, tieringName, tieringValue, dropdownName, dropdownValue } =
    props;
  const {
    values: { location },
  } = useContext(TeamMemberPageContext);
  const isMultiLocationTieringEnabled = useSelector(
    isMultiLocationTieringFeatureEnabledSelector(Number(location?.value)),
  );
  const { t } = useTranslation();
  const {
    data: { options = [], hasNextPage },
    loadMore,
  } = useSecondaryLocationFilterDataContext();

  const [dropdownData, setDropdownData] = useState(options);
  const prevOptionsRef = useRef(options);

  const getLocationLabel = useCallback(
    (location) => {
      const item = dropdownData.find((dropdownItem) => dropdownItem.key === location);
      return item ? item.label : '';
    },
    [dropdownData],
  );

  useEffect(() => {
    if (
      prevOptionsRef.current !== options &&
      JSON.stringify(prevOptionsRef.current) !== JSON.stringify(options)
    ) {
      setDropdownData((currentData) => mergeOptions(currentData, options));
      prevOptionsRef.current = options; // Update the ref to the new options
    }
  }, [options]);

  if (hasNextPage) {
    loadMore();
  }

  return (
    <Flex flexDirection="column">
      <Flex flexDirection="column" padding="0 0 20px 0">
        <LabelText style={{ fontWeight: 'normal', fontFamily: 'Poppins Medium' }}>
          <Flex gap="8px">
            {t('team_member:SECONDARY_LOCATIONS')}
            <BKJIconTooltip iconName="Info" placement="top">
              <TooltipWrapper>{t('team_member:SECONDARY_LOCATIONS_TOOLTIP_INFO')}</TooltipWrapper>
            </BKJIconTooltip>
          </Flex>
        </LabelText>
        <BKJPillDropdown
          name={dropdownName}
          type="Multi"
          value={dropdownValue}
          onChange={onChange}
          options={dropdownData}
          max={20}
          validations={[]}
          placeholder={i18next.t('team_member:SELECT_SECONDARY_LOCATIONS')}
        />
      </Flex>
      {isMultiLocationTieringEnabled &&
        HARDCODED_SHOW_SCHEDULING_OR_CALLOUT_ON_JANE_EDIT_VIEW_FOR_SECONDARY_LOCATIONS && (
          <Fragment>
            {Array.isArray(dropdownValue) && !!dropdownValue.length && (
              <Flex flexDirection="column" gap="12px">
                <SecondaryLocationSelectionLabel>
                  {t('team_member:SELECT_CALLOUT_OR_TIERING')}
                </SecondaryLocationSelectionLabel>

                {dropdownValue.map((location) => (
                  <Flex key={location} flexDirection="column">
                    <SecondaryLocationSelectionLabel>
                      {getLocationLabel(location)}
                    </SecondaryLocationSelectionLabel>
                    <Flex width="100%">
                      <BKJRadioInput
                        name={`${tieringName}_${location}`}
                        value="call_out"
                        onChange={tieringValueChange}
                        checked={tieringValue[location] === 'call_out'}
                        label="Call Out"
                        minWidth="33%"
                      />
                      <BKJRadioInput
                        name={`${tieringName}_${location}`}
                        value="scheduling"
                        onChange={tieringValueChange}
                        checked={tieringValue[location] === 'scheduling'}
                        label="Scheduling"
                        minWidth="33%"
                      />
                      <BKJRadioInput
                        name={`${tieringName}_${location}`}
                        value="both"
                        onChange={tieringValueChange}
                        checked={tieringValue[location] === 'both'}
                        label="Both"
                        minWidth="33%"
                      />
                    </Flex>
                  </Flex>
                ))}
              </Flex>
            )}
          </Fragment>
        )}
    </Flex>
  );
};

export const SecondaryLocationSection = (props: ISecondaryLocationSectionProps) => {
  return (
    <ErrorBoundary componentName="SecondaryLocationSection">
      <SecondaryLocationFilterDataProvider>
        <SecondaryLocationSectionComponent {...props} />
      </SecondaryLocationFilterDataProvider>
    </ErrorBoundary>
  );
};
